/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { useId, useRef, useState, type ReactNode } from "react";
import { useFocusRing, useTextField } from "react-aria";

import { useFormat } from "@superweb/intl";

import { useMessage } from "#intl";

import { useSubmitGuard } from "../experimental-form";
import { useUiColors } from "../theme";
import { ClearButton } from "./buttons";
import { Field, useTextInputStyle } from "./field";
import { FieldLabel } from "./label";
import { FieldErrorMessage } from "./error-message";
import { FieldDescription } from "./description";

type InvalidRules = {
  required: boolean;
  maxLength: boolean;
};

export type BaseTextFieldState = {
  value: string;
  errorVisible?: boolean;
  errorMessage?: string;
  invalid?: boolean;
};

export const BaseTextField = ({
  label,
  description,
  __experimentalWarning,
  ariaDescribedBy,
  type,
  placeholder,
  required = false,
  disabled = false,
  state,
  icon,
  autoFocus = false,
  toolbar,
  inputMode,
  maxLength,
  trim = false,
  onChange,
  validate,
}: {
  label: string;
  description?: string;
  __experimentalWarning?: string;
  ariaDescribedBy?: string;
  type?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  state: BaseTextFieldState;
  icon?: ReactNode;
  autoFocus?: boolean;
  toolbar?: ReactNode;
  inputMode?:
    | "text"
    | "tel"
    | "url"
    | "email"
    | "numeric"
    | "decimal"
    | "search";
  /**
   * The maximum number of characters.
   * Displayed in the character counter.
   */
  maxLength?: number;
  __experimentalCharacterCounter?: boolean;
  trim?: boolean;
  onChange: (
    state: BaseTextFieldState,
    info: {
      invalid: {
        /**
         * If `true`, the value is missing, but it's required.
         */
        required: boolean;

        /**
         * The maximum allowed length that a user may enter.
         */
        maxLength: boolean;
      };
    },
  ) => void;
  validate?: (state: BaseTextFieldState) => string | undefined;
}) => {
  const getErrorMessage = useErrorMessage(maxLength);
  const uiColors = useUiColors();
  const inputCss = useTextInputStyle({
    disabled,
    icon: Boolean(icon),
  });

  const ref = useRef<HTMLInputElement>(null);
  const [localValue, setLocalValue] = useState(state.value);

  const { isFocused, focusProps } = useFocusRing({
    isTextInput: true,
    within: true,
  });

  if (!isFocused && localValue !== state.value) {
    setLocalValue(state.value);
  }

  const characterCounterId = useId();

  const handleValidate = (state: BaseTextFieldState) => {
    const invalidRules = validateTextFieldValue({
      value: state.value,
      required,
      maxLength,
    });
    const customErrorMessage = validate?.(state);
    const errorMessage = Object.values(invalidRules).some(Boolean)
      ? getErrorMessage(invalidRules)
      : undefined;
    return {
      invalidRules: {
        ...invalidRules,
        custom: Boolean(customErrorMessage),
      },
      errorMessage: customErrorMessage || errorMessage,
    };
  };

  const handleChange = ({
    currentState,
  }: {
    currentState: BaseTextFieldState;
  }) => {
    const { invalidRules, errorMessage } = handleValidate(currentState);
    onChange(
      {
        ...currentState,
        errorMessage,
        errorVisible: currentState.errorVisible,
        invalid: Object.values(invalidRules).some(Boolean),
      },
      {
        invalid: {
          required: invalidRules.required,
          maxLength: invalidRules.maxLength,
        },
      },
    );
  };

  useSubmitGuard({
    validate: () => {
      const { invalidRules } = handleValidate(state);
      return !Object.values(invalidRules).some(Boolean);
    },
    onInvalid: () =>
      handleChange({ currentState: { ...state, errorVisible: true } }),
  });

  const { labelProps, inputProps, descriptionProps, errorMessageProps } =
    useTextField(
      {
        label,
        description: __experimentalWarning
          ? __experimentalWarning
          : description,
        "aria-describedby": [characterCounterId, ariaDescribedBy]
          .filter(Boolean)
          .join(" "),
        type,
        isRequired: required,
        isDisabled: disabled,
        value: localValue,
        validationState:
          state.errorVisible && state.errorMessage ? "invalid" : "valid",
        autoFocus,
        inputMode,
        onChange: (value: string) => {
          setLocalValue(value);

          const nextValue = trim ? value.trim() : value;

          handleChange({
            currentState: { ...state, value: nextValue, errorVisible: false },
          });
        },
        onBlur: () => {
          const nextValue = trim ? localValue.trim() : localValue;
          if (trim) {
            setLocalValue(nextValue);
          }

          handleChange({
            currentState: { ...state, value: nextValue, errorVisible: true },
          });
        },
      },
      ref,
    );

  const isShrunk = isFocused || Boolean(state.value);

  const onClearButtonPress = () => {
    ref.current?.focus();
    setLocalValue("");

    handleChange({
      currentState: {
        value: "",
        errorVisible: false,
      },
    });
  };

  return (
    <Field
      fieldProps={focusProps}
      shrunk={isShrunk}
      focused={isFocused}
      disabled={disabled}
      stretchInputElement={true}
      icon={icon}
      label={
        <FieldLabel shrunk={isShrunk} labelProps={labelProps}>
          {label}
        </FieldLabel>
      }
      input={
        <input
          {...inputProps}
          ref={ref}
          placeholder={placeholder}
          onChange={(e) => {
            if (!trim) {
              inputProps.onChange?.(e);
            } else {
              // With trim: compute cursor position in the input, apply the
              // trimmed value, then restore the caret position.
              const value = e.target.value;
              const trimmedValue = value.trimStart();
              if (trimmedValue !== value) {
                let newSelectionStart =
                  e.target.selectionStart !== null
                    ? e.target.selectionStart -
                      (value.length - trimmedValue.length)
                    : trimmedValue.length - 1;

                newSelectionStart =
                  newSelectionStart > 0 ? newSelectionStart : 0;

                e.target.value = trimmedValue;
                e.target.setSelectionRange(
                  newSelectionStart,
                  newSelectionStart,
                );
              }
              inputProps.onChange?.(e);
            }
          }}
          __experimental_placeholderCss={{
            color: uiColors.textMinor,
            opacity: isFocused ? "1" : "0",
            transitionDuration: isFocused ? "300ms" : undefined,
          }}
          css={inputCss}
        />
      }
      clearButton={
        <ClearButton
          visible={Boolean(state.value) && isFocused && !disabled}
          onPress={onClearButtonPress}
        />
      }
      toolbar={toolbar}
      descriptionAndError={
        state.errorVisible && state.errorMessage ? (
          <FieldErrorMessage errorMessageProps={errorMessageProps}>
            {state.errorMessage}
          </FieldErrorMessage>
        ) : (
          (__experimentalWarning || description) && (
            <FieldDescription
              descriptionProps={descriptionProps}
              variant={__experimentalWarning ? "warning" : "default"}
            >
              {__experimentalWarning ? __experimentalWarning : description}
            </FieldDescription>
          )
        )
      }
      onClick={() => {
        ref.current?.focus();
      }}
    />
  );
};

export const createBaseTextFieldState = <T extends BaseTextFieldState>(
  defaultValue?: T,
) => {
  return {
    value: "",
    errorVisible: false,
    errorMessage: undefined,
    ...defaultValue,
  };
};

export const useBaseTextFieldState = <T extends BaseTextFieldState>(
  defaultValue?: T,
) => {
  return useState<BaseTextFieldState>(createBaseTextFieldState(defaultValue));
};

const useErrorMessage = (maxLength?: number) => {
  const message = useMessage();
  const { formatInteger } = useFormat();

  return (invalidRules: InvalidRules) => {
    if (invalidRules.maxLength && maxLength !== undefined) {
      return message({
        id: "6a80357a-9f10-43ea-9692-b5f0df038772",
        context: "Base Text field. Max length error message",
        default:
          "Maximum length: {maxLength} {maxLength, plural, one {character} few {characters} other {characters}}",
        values: { maxLength: formatInteger(maxLength) },
      });
    }
    if (invalidRules.required) {
      return message({
        id: "2f4c2f7b-4ebc-40e3-8aa5-a50548c3b457",
        context:
          "Text field. Error message in platform libraries in the Text field component.",
        default: "Required field",
      });
    }

    return undefined;
  };
};

const validateTextFieldValue = ({
  value,
  required,
  maxLength,
}: {
  value: string;
  required?: boolean;
  maxLength?: number;
}) => {
  const invalid: InvalidRules = {
    required: Boolean(required && !value),
    maxLength: false,
  };

  if (!value) {
    return invalid;
  }

  if (maxLength !== undefined) {
    invalid.maxLength = value.length > maxLength;
  }

  return invalid;
};
